import { reactive } from 'vue'

/**
 * App-wide confirmation dialog, replacing the browser's native confirm().
 *
 * A single <ConfirmDialog> is mounted once (App.vue) and reads this shared
 * state; `confirm()` opens it and resolves a Promise<boolean> when the user
 * answers. Usage:
 *
 *   const { confirm } = useConfirm()
 *   if (!await confirm({ title: 'Löschen?', message: '…', danger: true })) return
 *
 * A bare string is accepted too: `await confirm('Fortfahren?')`.
 */
export interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  /** Red confirm button for destructive actions. Default true. */
  danger?: boolean
}

interface ConfirmState extends Required<Omit<ConfirmOptions, 'message'>> {
  open: boolean
  message: string
  _resolve: ((v: boolean) => void) | null
}

const state = reactive<ConfirmState>({
  open: false,
  title: '',
  message: '',
  confirmText: '',
  cancelText: '',
  danger: true,
  _resolve: null,
})

export const confirmState = state

/** Called by ConfirmDialog when the user answers (or the modal is dismissed). */
export function resolveConfirm(value: boolean) {
  state.open = false
  const r = state._resolve
  state._resolve = null
  r?.(value)
}

export function useConfirm() {
  function confirm(opts: ConfirmOptions | string): Promise<boolean> {
    const o: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts
    // Resolve any in-flight dialog as "false" before opening a new one.
    if (state._resolve) resolveConfirm(false)
    state.title = o.title || ''
    state.message = o.message
    state.confirmText = o.confirmText || ''
    state.cancelText = o.cancelText || ''
    state.danger = o.danger ?? true
    state.open = true
    return new Promise<boolean>((resolve) => { state._resolve = resolve })
  }
  return { confirm }
}
